// Compat shim that lets components keep the Vuelidate v0.x style of declaring
// `validations: { ... }` as a component option while running on
// @vuelidate/core v2.
//
// Important: Vue 3 does NOT run `setup()` from mixins — only from the
// component itself. So we cannot put useVuelidate() inside this mixin.
// Instead, each component must call `setup()` directly with `useSpinValidate()`
// (see below) and we keep the mixin for the `$v` alias and the safety Proxy.

import useVuelidate from '@vuelidate/core'
import { computed } from 'vue'

const noop = () => {}

const validationStubKeys = {
  $invalid: false,
  $error: false,
  $pending: false,
  $dirty: false,
  $anyError: false,
  $anyDirty: false,
  $errors: [],
  $silentErrors: [],
  $touch: noop,
  $reset: noop,
  $validate: () => Promise.resolve(false),
  $commit: noop,
  $clearExternalResults: noop
}

// Sentinel that pretends to be an empty/valid validation node so templates can
// safely access $v.user.password.$invalid before vuelidate has bound rules.
const validationSentinel = new Proxy(Object.freeze({}), {
  get (target, key) {
    if (key === Symbol.toPrimitive || key === Symbol.iterator) return undefined
    if (key === 'then') return undefined
    if (key in validationStubKeys) return validationStubKeys[key]
    if (typeof key === 'symbol') return target[key]
    return validationSentinel
  }
})

const wrapValidation = (raw) => {
  if (!raw || typeof raw !== 'object') return validationSentinel
  return new Proxy(raw, {
    get (target, key) {
      // Vuelidate 2 dropped $anyError (any touched descendant invalid); v2's $error on a parent
      // only turns true once every child is dirty, so derive it from $errors instead.
      if (key === '$anyError' && !(key in target)) {
        return (target.$errors || []).length > 0
      }
      if (key in target) {
        const value = target[key]
        if (value && typeof value === 'object' && !Array.isArray(value) && typeof value !== 'function') {
          if ('$invalid' in value || '$dirty' in value) {
            return wrapValidation(value)
          }
        }
        return value
      }
      if (typeof key === 'symbol') return target[key]
      if (key in validationStubKeys) return validationStubKeys[key]
      return validationSentinel
    }
  })
}

// Call this from the component's own setup(): `setup() { return useSpinValidate() }`.
// Returns { v$ } where v$ is wrapped to never throw on missing paths.
//
// $autoDirty: true is critical. In Vuelidate 1.x, $dirty flipped automatically
// whenever a v-modeled field changed. In Vuelidate 2.x the default behavior
// requires an explicit $touch() per field — anything without a @blur handler
// (selects, checkboxes, programmatic updates) never contributes to $anyDirty,
// which silently breaks save buttons gated on `$v.$anyDirty`.
export function useSpinValidate () {
  const inner = useVuelidate({ $autoDirty: true })
  return {
    v$: computed(() => wrapValidation(inner.value))
  }
}

// Mixin still provides the `$v` alias for templates that use `$v.user.foo`
// instead of `v$.user.foo`. setup() is intentionally NOT here — Vue 3 doesn't
// run mixin setup().
export const validationMixin = {
  computed: {
    $v () {
      return this.v$ || validationSentinel
    }
  }
}
